import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LZString from 'lz-string';
import './DFSManage.css';
import { useIsPrivilegedUser, loadSleeperAuth } from './auth';
import { decodeLineup } from './dfsLineup';

// Add a mock flag
const mock = process.env.REACT_APP_MOCK === 'true';

// Define the base URL based on the mock flag
const BASE_URL = mock
  ? "http://localhost:5000"
  : "https://shaggy-latashia-carnade-2ea2054a.koyeb.app";

// Clearing a lineup is organiser-only and the backend checks the Sleeper token,
// not just that the page rendered — so the request has to carry it.
function sleeperAuthHeaders() {
  const token = loadSleeperAuth()?.token;
  return token ? { Authorization: token } : {};
}

function DFSManage() {
  const navigate = useNavigate();
  // The button that reaches this page is already behind a privileged check, but the
  // route is reachable by URL, and this page can delete entrants. Gate the page too.
  const isPrivilegedUser = useIsPrivilegedUser();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entryDetails, setEntryDetails] = useState({});
  const [removingEntrant, setRemovingEntrant] = useState(null);
  const [purgePointsOnRemove, setPurgePointsOnRemove] = useState(false);
  // Keyed "{entry}:{username}" like removingEntrant and clearingLineup, rather
  // than overloading expandedEntries, whose keys are entry names.
  const [expandedHistory, setExpandedHistory] = useState(new Set());
  // One fetch per tournament serves every entrant in it.
  const [historyByEntry, setHistoryByEntry] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(new Set());
  const [savingLock, setSavingLock] = useState(null);
  const [clearingLineup, setClearingLineup] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(new Set());
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  // Per-player point overrides, keyed by week. They are global, not per
  // tournament — the picker is shown per entry only because that is where the
  // players who were actually used can be listed.
  // The nightly scrape can run before DailyFantasyFuel has published the slate,
  // which leaves the day with no salaries until the next scheduled attempt. This
  // is the manual nudge for when the data is visibly up but ours is not.
  const [refreshingDfs, setRefreshingDfs] = useState(false);
  const [dfsStatus, setDfsStatus] = useState(null);
  // The /admin routes are gated on an organiser's Sleeper login, so they are no
  // longer reachable from a browser tab. These are the ones worth a button.
  const [runningTask, setRunningTask] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const [overrideWeek, setOverrideWeek] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [slatePlayers, setSlatePlayers] = useState([]);
  const [overridePlayer, setOverridePlayer] = useState('');
  const [overridePoints, setOverridePoints] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);

  // Fetch list of all tinyURL entries
  const fetchEntries = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${BASE_URL}/tinyurl/list`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries || []);
      } else {
        setError('Failed to fetch entries list');
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch details for a specific entry
  const fetchEntryDetails = async (entryName) => {
    setLoadingDetails(prev => new Set(prev).add(entryName));
    try {
      const response = await fetch(`${BASE_URL}/tinyurl/${entryName}/details`);
      if (response.ok) {
        const data = await response.json();
        setEntryDetails(prev => ({
          ...prev,
          [entryName]: data
        }));
      } else {
        console.error(`Failed to fetch details for ${entryName}`);
      }
    } catch (error) {
      console.error(`Error fetching details for ${entryName}:`, error);
    } finally {
      setLoadingDetails(prev => {
        const next = new Set(prev);
        next.delete(entryName);
        return next;
      });
    }
  };

  // Removing an entrant serves two different situations. A guillotine elimination
  // should leave the weeks already played on the board, while someone who regrets
  // entering should leave no trace — hence the opt-in purge rather than a default.
  const removeEntrant = async (entryName, username) => {
    const suffix = purgePointsOnRemove
      ? 'Their accumulated points will also be deleted.'
      : 'Points from weeks already played will be kept.';
    if (!window.confirm(`Remove ${username} from ${entryName}?\n\n${suffix}`)) {
      return;
    }

    setRemovingEntrant(`${entryName}:${username}`);
    try {
      const query = purgePointsOnRemove ? '?purge_standings=true' : '';
      const response = await fetch(
        `${BASE_URL}/tinyurl/${encodeURIComponent(entryName)}/entrants/${encodeURIComponent(username)}${query}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        await fetchEntryDetails(entryName);
      } else {
        const error = await response.json().catch(() => ({}));
        window.alert(`Could not remove ${username}: ${error.error || response.statusText}`);
      }
    } catch (error) {
      window.alert(`Could not remove ${username}: ${error.message}`);
    } finally {
      setRemovingEntrant(null);
    }
  };

  const readDfsStatus = async () => {
    try {
      const stats = await fetch(`${BASE_URL}/statistics`).then((r) => r.json());
      return {
        updated: stats.last_dfs_salaries_update,
        attempted: stats.last_dfs_salaries_attempt,
        error: stats.dfs_salaries_error,
        retries: stats.dfs_salaries_retries_queued,
      };
    } catch {
      return null;
    }
  };

  // The scrape outlives the hosting gateway's 100-second limit, so the endpoint
  // returns 202 and works in the background. Watch /statistics for the outcome
  // rather than waiting on the request, which would look like a failure.
  const refreshDfsSalaries = async () => {
    const before = await readDfsStatus();
    const last = before?.updated && before.updated !== 'Never' ? before.updated : 'never';
    if (!window.confirm(
      'Fetch DFS salaries now?\n\n' +
      `Last successful update: ${last}.\n\n` +
      'It takes several minutes and runs in the background — this page will keep ' +
      'checking and tell you how it went. Worth doing when the slate is up on ' +
      'DailyFantasyFuel but the scheduled run has not picked it up yet.'
    )) {
      return;
    }

    setRefreshingDfs(true);
    setDfsStatus({ state: 'working', message: 'Scraping — this takes a few minutes.' });
    try {
      const response = await fetch(`${BASE_URL}/admin/dfs-salaries/update`, {
        method: 'POST',
        headers: sleeperAuthHeaders(),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || response.statusText);
      }
    } catch (error) {
      setRefreshingDfs(false);
      setDfsStatus({ state: 'failed', message: `Could not start: ${error.message}` });
      return;
    }

    // What to watch is not the attempt timestamp: that is stamped when the
    // scrape starts, so it moves within a second of the request and would read
    // as a finished run while the thing is still going. A success moves
    // last_dfs_salaries_update; a failure records an error and queues a retry.
    const beforeUpdated = before?.updated ?? null;
    const beforeError = before?.error ?? null;
    const beforeRetries = before?.retries ?? 0;
    const deadline = Date.now() + 10 * 60 * 1000;

    const check = async () => {
      if (Date.now() > deadline) {
        setRefreshingDfs(false);
        setDfsStatus({
          state: 'unknown',
          message: 'Still going after ten minutes — check /statistics for the outcome.',
        });
        return;
      }

      const now = await readDfsStatus();
      if (now) {
        if (now.updated && now.updated !== beforeUpdated) {
          setRefreshingDfs(false);
          setDfsStatus({ state: 'done', message: `Salaries updated ${now.updated}.` });
          return;
        }
        const failed =
          (now.error && now.error !== beforeError) || (now.retries ?? 0) > beforeRetries;
        if (failed) {
          setRefreshingDfs(false);
          setDfsStatus({
            state: 'failed',
            message: `Scrape failed: ${now.error || 'no reason recorded'}` +
              (now.retries ? ` — ${now.retries} retry queued.` : ''),
          });
          return;
        }
      }
      setTimeout(check, 15000);
    };
    setTimeout(check, 15000);
  };

  // A plain fire-and-report admin action. The destructive one gets a confirm
  // that says what it will do rather than asking "are you sure".
  const runMaintenance = async ({ id, label, path, confirm }) => {
    if (confirm && !window.confirm(confirm)) return;

    setRunningTask(id);
    setTaskStatus(null);
    try {
      const response = await fetch(`${BASE_URL}${path}`, {
        method: 'POST',
        headers: sleeperAuthHeaders(),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || response.statusText);
      setTaskStatus({
        state: 'done',
        message: body.message || `${label} finished.`,
      });
    } catch (error) {
      setTaskStatus({ state: 'failed', message: `${label} failed: ${error.message}` });
    } finally {
      setRunningTask(null);
    }
  };

  const MAINTENANCE = [
    {
      id: 'fantasy-points',
      label: 'Refresh FantasyData points',
      path: '/admin/fantasy-points/update',
      confirm: 'Refresh FantasyData points now?\n\nUsed as a fallback when Sleeper has '
        + 'no score for a player. Safe to run at any time.',
    },
    {
      id: 'rankings',
      label: 'Refresh dynasty rankings',
      path: '/admin/rankings/update',
      confirm: 'Refresh dynasty rankings now?\n\nRe-scrapes KTC and FantasyCalc values. '
        + 'Safe to run at any time.',
    },
    {
      id: 'cleanup',
      label: 'Run tournament cleanup',
      path: '/admin/tinyurl/cleanup',
      destructive: true,
      confirm: 'Run tournament cleanup now?\n\nThis is the Wednesday/Thursday pass, and it '
        + 'is not a dry run. It scores the week that has finished, DELETES every lineup it '
        + 'scored, advances each tournament a week, and removes tournaments that have ended.\n\n'
        + 'Set any points overrides before running it — once the lineups are gone the week '
        + 'cannot be recomputed.',
    },
  ];

  // Clearing exists for the lineup its owner can no longer fix: once one of its
  // players has kicked off, submitting over it is refused. This wipes only the
  // current week's lineup — they stay an entrant and keep points from earlier
  // weeks — so they can enter a fresh one.
  // The last ten submissions for the week in progress. Organiser-only, and a
  // separate route from the admin data dump so this page never holds PINs.
  const fetchHistory = async (entryName) => {
    setLoadingHistory(prev => new Set(prev).add(entryName));
    try {
      const response = await fetch(
        `${BASE_URL}/admin/tinyurl/${encodeURIComponent(entryName)}/history`,
        { headers: sleeperAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setHistoryByEntry(prev => ({ ...prev, [entryName]: data }));
      } else {
        const body = await response.json().catch(() => ({}));
        window.alert(`Could not load history: ${body.error || response.status}`);
      }
    } catch (error) {
      window.alert(`Could not load history: ${error.message}`);
    } finally {
      setLoadingHistory(prev => {
        const next = new Set(prev);
        next.delete(entryName);
        return next;
      });
    }
  };

  const toggleHistory = (entryName, username) => {
    const key = `${entryName}:${username}`;
    setExpandedHistory(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (!historyByEntry[entryName]) fetchHistory(entryName);
  };

  // Which players a stored lineup held.
  //
  // Tokens rather than resolved players, because a defence is a team code and a
  // manual entry is a name. slatePlayers is the override panel's own list and
  // is loaded for one week only, so it may be cold — then the raw token shows,
  // which is still readable.
  const slateNames = {};
  slatePlayers.forEach((player) => { slateNames[player.id] = player.name; });

  const describeLineup = (data) => {
    const decoded = decodeLineup(data);
    if (!decoded) return null;
    return decoded.players.map(({ token }) => slateNames[token] || token);
  };

  const setLineupLock = async (entryName, mode) => {
    setSavingLock(entryName);
    try {
      const response = await fetch(
        `${BASE_URL}/admin/tinyurl/${encodeURIComponent(entryName)}/settings`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...sleeperAuthHeaders() },
          body: JSON.stringify({ lineup_lock: mode })
        }
      );
      if (response.ok) {
        await fetchEntryDetails(entryName);
      } else {
        const body = await response.json().catch(() => ({}));
        window.alert(`Could not change the lock: ${body.error || response.status}`);
      }
    } catch (error) {
      window.alert(`Could not change the lock: ${error.message}`);
    } finally {
      setSavingLock(null);
    }
  };

  const clearLineup = async (entryName, username) => {
    if (!window.confirm(
      `Clear ${username}'s lineup in ${entryName}?\n\n` +
      'Only this week\'s lineup goes. They stay in the tournament and keep points ' +
      'from earlier weeks, but the week scores as nothing until they submit again — ' +
      'which they can do even if games have already started.'
    )) {
      return;
    }

    setClearingLineup(`${entryName}:${username}`);
    try {
      const response = await fetch(
        `${BASE_URL}/tinyurl/${encodeURIComponent(entryName)}/entrants/${encodeURIComponent(username)}/lineup`,
        { method: 'DELETE', headers: sleeperAuthHeaders() }
      );
      if (response.ok) {
        await fetchEntryDetails(entryName);
      } else {
        const error = await response.json().catch(() => ({}));
        window.alert(`Could not clear ${username}'s lineup: ${error.error || response.statusText}`);
      }
    } catch (error) {
      window.alert(`Could not clear ${username}'s lineup: ${error.message}`);
    } finally {
      setClearingLineup(null);
    }
  };

  // Every player on the week's slate, by name. The salary rows are keyed
  // "{id}_W{week}_D{date}", so they are indexed by their sleeper_id field rather
  // than by a reconstructed key.
  const loadOverrides = useCallback(async (week) => {
    if (!week) return;
    try {
      const [salaryRes, overrideRes] = await Promise.all([
        fetch(`${BASE_URL}/dfs-salaries/week/${week}`).then((r) => (r.ok ? r.json() : {})),
        fetch(`${BASE_URL}/points-overrides/week/${week}`).then((r) => (r.ok ? r.json() : { overrides: {} })),
      ]);

      const byId = new Map();
      Object.values(salaryRes || {}).forEach((row) => {
        const id = row?.sleeper_id;
        if (!id || byId.has(id)) return;
        byId.set(id, {
          id,
          name: row.name || id,
          position: row.position || '',
          team: row.team || '',
        });
      });

      setSlatePlayers([...byId.values()].sort((a, b) => a.name.localeCompare(b.name)));
      setOverrides(overrideRes.overrides || {});
    } catch {
      setSlatePlayers([]);
    }
  }, []);

  const saveOverride = async () => {
    const chosen = slatePlayers.find(
      (p) => p.name.toLowerCase() === overridePlayer.trim().toLowerCase()
    );
    if (!chosen) {
      window.alert('Pick a player from the list.');
      return;
    }
    const points = Number(overridePoints);
    if (overridePoints === '' || Number.isNaN(points)) {
      window.alert('Enter a number of points.');
      return;
    }
    const week = overrideWeek;
    const sleeperId = chosen.id;
    setSavingOverride(true);
    try {
      const res = await fetch(`${BASE_URL}/points-overrides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...sleeperAuthHeaders() },
        body: JSON.stringify({ sleeper_id: sleeperId, week, points }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        window.alert(`Could not set it: ${body.error || res.statusText}`);
        return;
      }
      setOverridePlayer('');
      setOverridePoints('');
      await loadOverrides(week);
    } catch (err) {
      window.alert(`Could not set it: ${err.message}`);
    } finally {
      setSavingOverride(false);
    }
  };

  const removeOverride = async (week, sleeperId) => {
    setSavingOverride(true);
    try {
      const res = await fetch(`${BASE_URL}/points-overrides/${sleeperId}/${week}`, {
        method: 'DELETE',
        headers: sleeperAuthHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        window.alert(`Could not remove it: ${body.error || res.statusText}`);
        return;
      }
      await loadOverrides(week);
    } catch (err) {
      window.alert(`Could not remove it: ${err.message}`);
    } finally {
      setSavingOverride(false);
    }
  };

  useEffect(() => {
    if (overrideWeek) return;
    const weeks = Object.values(entryDetails).map((d) => d?.week).filter(Boolean);
    if (weeks.length === 0) return;
    const week = Math.max(...weeks);
    setOverrideWeek(week);
    loadOverrides(week);
  }, [entryDetails, overrideWeek, loadOverrides]);

  // Fetch details for all entries
  useEffect(() => {
    if (isPrivilegedUser) {
      fetchEntries();
    }
  }, [isPrivilegedUser]);

  useEffect(() => {
    if (entries.length > 0) {
      entries.forEach(entry => {
        fetchEntryDetails(entry.name);
      });
      // Initialize all entries as expanded
      setExpandedEntries(new Set(entries.map(entry => entry.name)));
    }
  }, [entries]);

  const toggleEntry = (entryName) => {
    setExpandedEntries(prev => {
      const next = new Set(prev);
      if (next.has(entryName)) {
        next.delete(entryName);
      } else {
        next.add(entryName);
      }
      return next;
    });
  };

  const getSubmissionSummary = (details) => {
    if (!details || !details.submissions || !details.allowed_names) {
      return { submitted: 0, total: 0 };
    }
    const total = details.allowed_names.length;
    const submitted = details.allowed_names.filter(
      username => details.submissions[username]?.has_submitted
    ).length;
    return { submitted, total };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateWithWeekday = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
      const dateTime = date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      return `${weekday} ${dateTime}`;
    } catch (error) {
      return dateString;
    }
  };

  const copyLeagueData = async (entryName, details) => {
    try {
      if (!details || !details.allowed_names || details.allowed_names.length === 0) {
        alert('No users found in this league');
        return;
      }

      // Fetch the full data for this entry (contains user_submissions)
      // action=results skips the PIN prompt; the token is what gets the
      // lineups themselves before a tournament's reveal time, which is the
      // whole point of copying them from here.
      const response = await fetch(
        `${BASE_URL}/tinyurl/${entryName}/data?action=results`,
        { headers: sleeperAuthHeaders() }
      );
      if (!response.ok) {
        alert('Failed to fetch league data');
        return;
      }

      const data = await response.json();
      const userSubmissions = data.user_submissions || {};
      const allowedNames = data.allowed_names || details.allowed_names || [];

      // Process each user's submission data
      const userDataMap = new Map();
      
      Object.values(userSubmissions).forEach(submission => {
        if (submission && submission.data) {
          try {
            const username = submission.username;
            const compressedData = submission.data; // Format: "week|compressedData"
            
            // Parse the compressed data
            const [, compressed] = compressedData.split('|');
            
            // Decompress using LZString
            const decompressed = LZString.decompressFromEncodedURIComponent(compressed);
            
            if (decompressed) {
              // The decompressed data should be in format "username:decodedData"
              // Extract just the decoded data part
              const colonIndex = decompressed.indexOf(':');
              if (colonIndex !== -1) {
                const decodedData = decompressed.substring(colonIndex + 1);
                // Encode back to base64
                const encodedData = btoa(decodedData);
                userDataMap.set(username.toLowerCase(), encodedData);
              } else {
                // If no colon, the entire decompressed string is the data
                const encodedData = btoa(decompressed);
                userDataMap.set(username.toLowerCase(), encodedData);
              }
            }
          } catch (error) {
            console.error(`Error processing data for ${submission.username}:`, error);
            // Continue with other users even if one fails
          }
        }
      });

      // Build the output: iterate through allowed_names in order
      const lines = allowedNames.map(username => {
        // Try both original case and lowercase for matching
        const encodedData = userDataMap.get(username.toLowerCase()) || userDataMap.get(username);
        if (encodedData) {
          return `${username}:${encodedData}`;
        } else {
          return `${username}:`;
        }
      });

      navigator.clipboard.writeText(lines.join('\n'));
      alert('League data copied to clipboard!');
    } catch (error) {
      console.error('Error copying league data:', error);
      alert('Failed to copy league data');
    }
  };

  if (!isPrivilegedUser) {
    return (
      <div className="dfs-manage-container">
        <h1 className="dfs-manage-title">DFS TinyURL Management</h1>
        <div className="dfs-manage-error">
          This page is only available when signed in with an authorised Sleeper
          account. Connect yours from Settings.
        </div>
        <button onClick={() => navigate('/settings')} className="dfs-manage-retry-btn">
          Go to Settings
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dfs-manage-container">
        <div className="dfs-manage-loading">Loading entries...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dfs-manage-container">
        <div className="dfs-manage-error">{error}</div>
        <button onClick={fetchEntries} className="dfs-manage-retry-btn">Retry</button>
      </div>
    );
  }

  return (
    <div className="dfs-manage-container">
      <h1 className="dfs-manage-title">DFS TinyURL Management</h1>

      <div className="dfs-manage-tools">
        <button
          type="button"
          className="dfs-manage-refresh-dfs"
          onClick={refreshDfsSalaries}
          disabled={refreshingDfs}
        >
          {refreshingDfs ? 'Fetching DFS salaries…' : 'Fetch DFS salaries now'}
        </button>
        {MAINTENANCE.map((task) => (
          <button
            key={task.id}
            type="button"
            className={`dfs-manage-refresh-dfs${task.destructive ? ' is-destructive' : ''}`}
            onClick={() => runMaintenance(task)}
            disabled={runningTask !== null}
          >
            {runningTask === task.id ? `${task.label}…` : task.label}
          </button>
        ))}
        {dfsStatus && (
          <span className={`dfs-manage-refresh-status is-${dfsStatus.state}`}>
            {dfsStatus.message}
          </span>
        )}
        {taskStatus && (
          <span className={`dfs-manage-refresh-status is-${taskStatus.state}`}>
            {taskStatus.message}
          </span>
        )}
      </div>

      {overrideWeek && (
        <div className="dfs-manage-override-panel">
          <h2 className="dfs-manage-section-title">Point overrides — week {overrideWeek}</h2>
          <p className="dfs-manage-override-note">
            A player nobody rosters in the scoring leagues counts as zero. A figure set
            here counts instead — for every tournament and on the results page alike.
            It has to be set before the week is scored on Wednesday.
          </p>

          {Object.keys(overrides).length > 0 && (
            <div className="dfs-manage-override-current">
              {Object.entries(overrides).map(([id, pts]) => {
                const known = slatePlayers.find((p) => p.id === id);
                return (
                  <div key={id} className="dfs-manage-override-row">
                    <span>{known ? known.name : id} — <strong>{pts}</strong></span>
                    <button
                      type="button"
                      className="dfs-manage-clear-lineup"
                      onClick={() => removeOverride(overrideWeek, id)}
                      disabled={savingOverride}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="dfs-manage-override-set">
            <input
              type="text"
              list="dfs-override-players"
              className="dfs-manage-override-input"
              placeholder="Search a player…"
              value={overridePlayer}
              onChange={(e) => setOverridePlayer(e.target.value)}
            />
            <datalist id="dfs-override-players">
              {slatePlayers.map((p) => (
                <option key={p.id} value={p.name}>
                  {[p.position, p.team].filter(Boolean).join(' · ')}
                </option>
              ))}
            </datalist>
            <input
              type="number"
              step="0.1"
              className="dfs-manage-override-points"
              placeholder="pts"
              value={overridePoints}
              onChange={(e) => setOverridePoints(e.target.value)}
            />
            <button
              type="button"
              className="dfs-manage-clear-lineup"
              onClick={saveOverride}
              disabled={savingOverride}
            >
              {savingOverride ? '…' : 'Set'}
            </button>
          </div>
          {slatePlayers.length === 0 && (
            <p className="dfs-manage-override-note">
              No salary data for week {overrideWeek}, so there are no names to search.
            </p>
          )}
        </div>
      )}

      {entries.length === 0 ? (
        <div className="dfs-manage-empty">No entries found</div>
      ) : (
        <div className="dfs-manage-entries">
          {entries.map((entry) => {
            const details = entryDetails[entry.name];
            const isLoading = loadingDetails.has(entry.name);
            const isExpanded = expandedEntries.has(entry.name);
            const summary = getSubmissionSummary(details);
            
            return (
              <div key={entry.name} className="dfs-manage-entry">
                <div 
                  className="dfs-manage-entry-header"
                  onClick={() => toggleEntry(entry.name)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="dfs-manage-entry-header-left">
                    <h2 className="dfs-manage-entry-name">{entry.name}</h2>
                    {details && summary.total > 0 && (
                      <span className="dfs-manage-entry-summary">
                        {summary.submitted} of {summary.total} lineups added
                      </span>
                    )}
                  </div>
                  <div className="dfs-manage-entry-meta">
                    <span className="dfs-manage-entry-date">
                      Created: {formatDate(entry.created_at)}
                    </span>
                    <div className="dfs-manage-entry-buttons">
                      <button 
                        className="dfs-manage-results-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dfs/results/tinyurl/${entry.name}?admin=true`);
                        }}
                        aria-label="View results"
                        title="View results page"
                      >
                        <svg 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                        >
                          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                          <polyline points="10 17 15 12 10 7"></polyline>
                          <line x1="15" y1="12" x2="3" y2="12"></line>
                        </svg>
                        <span className="dfs-manage-results-btn-text">Results</span>
                      </button>
                      {details && (
                        <button 
                          className="dfs-manage-copy-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyLeagueData(entry.name, details);
                          }}
                          aria-label="Copy league data"
                          title="Copy league data to clipboard"
                        >
                          <svg 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                          >
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        </button>
                      )}
                      <button 
                        className={`dfs-manage-toggle-btn ${isExpanded ? 'expanded' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleEntry(entry.name);
                        }}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        <svg 
                          width="16" 
                          height="16" 
                          viewBox="0 0 16 16" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                        >
                          <path d="M4 6 L8 10 L12 6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <>
                    {isLoading ? (
                      <div className="dfs-manage-loading-details">Loading details...</div>
                    ) : details ? (
                      <div className="dfs-manage-entry-details">
                    {details.week && (
                      <div className="dfs-manage-detail-row">
                        <span className="dfs-manage-detail-label">Week:</span>
                        <span className="dfs-manage-detail-value">
                          {details.week}
                          {details.tournament_week
                            ? ` (week ${details.tournament_week} of ${details.num_weeks})`
                            : ''}
                        </span>
                      </div>
                    )}

                    <div className="dfs-manage-override-panel">
                      <h3 className="dfs-manage-section-title">Lineup lock</h3>
                      <p className="dfs-manage-override-note">
                        What happens once one of an entrant&apos;s players has kicked off.
                        Allowing the rest to change lets somebody restructure mid-week
                        knowing how their early players did.
                      </p>
                      {['full', 'unplayed'].map((mode) => (
                        <label key={mode} className="dfs-manage-lock-option">
                          <input
                            type="radio"
                            name={`lineup-lock-${entry.name}`}
                            checked={(details.lineup_lock || 'full') === mode}
                            disabled={savingLock === entry.name}
                            onChange={() => setLineupLock(entry.name, mode)}
                          />
                          {mode === 'full'
                            ? 'Freeze the whole lineup once any player has played'
                            : 'Freeze only the players who have played'}
                        </label>
                      ))}
                    </div>

                    {(() => {
                      // An open tournament has no allowlist in its first week: the
                      // entrants are whoever has actually submitted.
                      const entrants = details.allowed_names && details.allowed_names.length > 0
                        ? details.allowed_names
                        : Object.keys(details.submissions || {});
                      if (entrants.length === 0) return null;
                      return (
                      <div className="dfs-manage-detail-section">
                        <h3 className="dfs-manage-section-title">
                          {details.access_mode === 'sleeper' ? 'Entrants (open to Sleeper logins)' : 'Allowed Users'}
                        </h3>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px',
                                        fontSize: '0.8rem', opacity: 0.8, marginBottom: '8px' }}>
                          <input
                            type="checkbox"
                            checked={purgePointsOnRemove}
                            onChange={(e) => setPurgePointsOnRemove(e.target.checked)}
                          />
                          Also delete accumulated points when removing (leave off to eliminate
                          a player but keep their played weeks)
                        </label>
                        <div className="dfs-manage-submissions">
                          {entrants.map((username) => {
                            const submission = details.submissions?.[username];
                            const hasSubmitted = submission?.has_submitted || false;
                            const updateCount = submission?.update_count || 0;
                            
                            return (
                              <div key={username} className={`dfs-manage-submission ${hasSubmitted ? 'submitted' : 'not-submitted'}`}>
                                <div className="dfs-manage-submission-content">
                                  <div className="dfs-manage-submission-left">
                                    <span className="dfs-manage-submission-username">{username}</span>
                                    <span className={`dfs-manage-submission-status ${hasSubmitted ? 'submitted' : 'not-submitted'}`}>
                                      {hasSubmitted ? '✓ Submitted' : '○ Not Submitted'}
                                    </span>
                                  </div>
                                  {submission && (
                                    <div className="dfs-manage-submission-right">
                                      <span className="dfs-manage-submission-meta-item">
                                        Updates: <span className="dfs-manage-submission-value">{updateCount}</span>
                                      </span>
                                      {submission.created_at && (
                                        <span className="dfs-manage-submission-meta-item">
                                          Created: <span className="dfs-manage-submission-value">{formatDateWithWeekday(submission.created_at)}</span>
                                        </span>
                                      )}
                                      {submission.updated_at && (
                                        <span className="dfs-manage-submission-meta-item">
                                          Updated: <span className="dfs-manage-submission-value">{formatDateWithWeekday(submission.updated_at)}</span>
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {hasSubmitted && (
                                    <button
                                      className="dfs-manage-clear-lineup"
                                      onClick={() => clearLineup(entry.name, username)}
                                      disabled={clearingLineup === `${entry.name}:${username}`}
                                      title={`Clear ${username}'s lineup so they can submit a new one`}
                                    >
                                      {clearingLineup === `${entry.name}:${username}` ? '…' : 'Clear'}
                                    </button>
                                  )}
                                  {updateCount > 1 && (
                                    <button
                                      className="dfs-manage-clear-lineup"
                                      onClick={() => toggleHistory(entry.name, username)}
                                      title={`What ${username} submitted earlier this week`}
                                    >
                                      {loadingHistory.has(entry.name)
                                        ? '…'
                                        : expandedHistory.has(`${entry.name}:${username}`)
                                        ? 'Hide history'
                                        : 'History'}
                                    </button>
                                  )}
                                  <button
                                    className="dfs-manage-remove-entrant"
                                    onClick={() => removeEntrant(entry.name, username)}
                                    disabled={removingEntrant === `${entry.name}:${username}`}
                                    title={`Remove ${username} from this tournament`}
                                  >
                                    {removingEntrant === `${entry.name}:${username}` ? '…' : 'Remove'}
                                  </button>
                                </div>
                                {expandedHistory.has(`${entry.name}:${username}`) && (() => {
                                  const records = historyByEntry[entry.name]?.entrants?.[username]?.history;
                                  if (!records) return null;
                                  if (records.length === 0) {
                                    return <p className="dfs-manage-history-note">Nothing kept for this week yet.</p>;
                                  }
                                  return (
                                    <ol className="dfs-manage-history">
                                      {records.map((record, i) => {
                                        const names = describeLineup(record.data);
                                        return (
                                          <li key={`${record.at}-${i}`} className="dfs-manage-history-row">
                                            <span className="dfs-manage-submission-value">
                                              {formatDateWithWeekday(record.at)}
                                            </span>
                                            {i === 0 && <span className="dfs-manage-history-tag">current</span>}
                                            <span className="dfs-manage-history-players">
                                              {names ? names.join(', ') : 'could not be read'}
                                            </span>
                                          </li>
                                        );
                                      })}
                                    </ol>
                                  );
                                })()}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      );
                    })()}

                    {details.updated_at && (
                      <div className="dfs-manage-detail-row">
                        <span className="dfs-manage-detail-label">Last Updated:</span>
                        <span className="dfs-manage-detail-value">
                          {formatDate(details.updated_at)}
                          {details.updated_by && ` by ${details.updated_by}`}
                        </span>
                      </div>
                    )}
                      </div>
                    ) : (
                      <div className="dfs-manage-error-details">Failed to load details</div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DFSManage;

